/**
 * Settings → Time & Leave → Biometric push devices (migration 083/084).
 *
 * The push-device registry: register a ZKTeco / eSSL / Matrix device that
 * pushes punches directly to WorkRight (no file upload). On create + rotate
 * the RAW device key is shown ONCE (copy + "store it now" warning, like API
 * tokens) — only the prefix is ever shown again. Each device gets the two
 * push URLs to point the machine at.
 *
 * Live push ingest is Pro-gated server-side (the public_api feature; file
 * import stays free). The REGISTRY itself is reachable on any time_tracking
 * plan, so when push is off we still let HR register devices but show a
 * prominent upgrade hint that pushes won't be accepted until they upgrade.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Router as RouterIcon,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { toApiError, API_BASE_URL } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useFeatures } from "@/lib/auth";
import {
  useCreateDevice,
  useDeactivateDevice,
  useDevices,
  useRotateDeviceKey,
} from "@/hooks/use-attendance-import";
import {
  ATT_DEVICE_VENDORS,
  ATT_DEVICE_VENDOR_LABELS,
  type AttDeviceVendor,
  type AttendanceDevice,
} from "@/types/attendance-import";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/attendance-devices",
)({
  component: DevicesRoute,
});

const PUSH_URL = `${API_BASE_URL}/api/v1/integrations/attendance/punches`;
const ICLOCK_URL = `${API_BASE_URL}/api/v1/integrations/attendance/iclock/cdata`;

function copy(value: string, label: string) {
  void navigator.clipboard.writeText(value);
  toast.success(`${label} copied`);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DevicesRoute() {
  const { has } = useFeatures();
  const pushEnabled = has("public_api");
  const q = useDevices();
  const devices = q.data?.devices ?? [];
  const [revealed, setRevealed] = useState<{
    key: string;
    name: string;
  } | null>(null);

  const activeDevices = devices.filter((d) => d.is_active);
  const inactiveDevices = devices.filter((d) => !d.is_active);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Biometric Push Devices</h2>
            <p className="text-muted-foreground text-sm">
              Register a ZKTeco / eSSL / Matrix device to push punches directly
              to WorkRight — no daily file upload. Each device authenticates
              with its own key.
            </p>
          </div>

          {!pushEnabled && (
            <Alert>
              <ShieldCheck aria-hidden className="h-4 w-4" />
              <AlertTitle>Live device push is a Pro feature</AlertTitle>
              <AlertDescription>
                You can register devices now, but punches pushed by them are
                rejected until your plan includes live device push. In the
                meantime, use{" "}
                <span className="font-medium">Attendance → Import</span> to
                upload your machine&apos;s export file — that&apos;s free on
                every plan.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="flex flex-col gap-3 space-y-0 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <RouterIcon aria-hidden className="h-4 w-4 text-primary" />
                  Registered Devices
                </CardTitle>
                <CardDescription>
                  Rotate a key if it leaks; deactivate to stop a device.
                </CardDescription>
              </div>
              <CreateDeviceDialog
                onCreated={(key, name) => setRevealed({ key, name })}
              />
            </CardHeader>
            <CardContent>
              {q.isLoading && (
                <div className="space-y-2">
                  {["d1", "d2"].map((k) => (
                    <Skeleton key={k} className="h-16 w-full rounded-md" />
                  ))}
                </div>
              )}
              {q.isError && (
                <Alert variant="destructive">
                  <AlertTitle>Could not load devices</AlertTitle>
                  <AlertDescription>
                    {toApiError(q.error).message}
                  </AlertDescription>
                </Alert>
              )}
              {!q.isLoading && !q.isError && devices.length === 0 && (
                <EmptyState
                  title="No Devices Registered"
                  description="Register your first biometric device to push punches automatically."
                />
              )}
              {!q.isLoading && !q.isError && activeDevices.length > 0 && (
                <div className="space-y-2">
                  {activeDevices.map((d) => (
                    <DeviceRow
                      key={d.id}
                      device={d}
                      onRotated={(key, name) => setRevealed({ key, name })}
                    />
                  ))}
                </div>
              )}
              {inactiveDevices.length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    {inactiveDevices.length} inactive device
                    {inactiveDevices.length === 1 ? "" : "s"}
                  </summary>
                  <div className="mt-2 space-y-2 opacity-60">
                    {inactiveDevices.map((d) => (
                      <DeviceRow
                        key={d.id}
                        device={d}
                        onRotated={(key, name) => setRevealed({ key, name })}
                      />
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>

          <PushUrlsCard />

          {revealed && (
            <RevealKeyDialog
              rawKey={revealed.key}
              deviceName={revealed.name}
              onClose={() => setRevealed(null)}
            />
          )}
        </div>
      </SettingsShell>
    </>
  );
}

// ---------------------------------------------------------------------------
// Push URLs reference
// ---------------------------------------------------------------------------

function PushUrlsCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Point Your Device at These URLs
        </CardTitle>
        <CardDescription>
          Configure your ZKTeco / eSSL device&apos;s &quot;cloud / ADMS
          server&quot; settings with one of these and the device key.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <UrlRow
          title="Generic JSON Push"
          url={PUSH_URL}
          hint="For our generic JSON punch endpoint (custom middleware / scripts)."
        />
        <UrlRow
          title="ZKTeco / eSSL iclock (ADMS)"
          url={ICLOCK_URL}
          hint="Set this as the device's ADMS / cloud server address (iclock protocol)."
        />
        <p className="text-xs text-muted-foreground">
          Send the device key in the{" "}
          <code className="rounded bg-muted px-1 text-foreground">Authorization: Bearer</code>{" "}
          header (generic push) or as the device&apos;s configured key for the
          iclock protocol. Each key is bound to one device and can only write
          your company&apos;s attendance.
        </p>
      </CardContent>
    </Card>
  );
}

function UrlRow({
  title,
  url,
  hint,
}: Readonly<{ title: string; url: string; hint: string }>) {
  return (
    <div className="space-y-1.5">
      <p className="font-medium">{title}</p>
      <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
        <code className="flex-1 break-all font-mono text-xs">{url}</code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={`Copy ${title} URL`}
          onClick={() => copy(url, "URL")}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device row
// ---------------------------------------------------------------------------

function DeviceRow({
  device,
  onRotated,
}: {
  readonly device: AttendanceDevice;
  readonly onRotated: (rawKey: string, name: string) => void;
}) {
  const rotate = useRotateDeviceKey();
  const deactivate = useDeactivateDevice();

  return (
    <div className="flex items-start justify-between gap-4 rounded-md border px-4 py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{device.name}</span>
          {device.vendor && (
            <Badge variant="secondary" className="text-xs">
              {device.vendor}
            </Badge>
          )}
          {device.is_active ? (
            <Badge variant="outline" className="text-xs">
              Active
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs">
              Inactive
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {device.device_identifier && (
            <span>ID: {device.device_identifier}</span>
          )}
          {device.api_key_prefix && (
            <span className="font-mono">key {device.api_key_prefix}…</span>
          )}
          <span>
            {device.last_seen_at
              ? `Last seen ${formatDateTime(device.last_seen_at)}`
              : "Never connected"}
          </span>
        </div>
      </div>
      {device.is_active && (
        <div className="flex shrink-0 items-center gap-1">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={rotate.isPending}
                aria-label={`Rotate key for ${device.name}`}
              >
                {rotate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rotate device key?</AlertDialogTitle>
                <AlertDialogDescription>
                  The current key for <strong>{device.name}</strong> stops
                  working immediately. You&apos;ll get a new key to configure on
                  the device. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    rotate.mutate(device.id, {
                      onSuccess: (res) =>
                        onRotated(res.raw_key, res.device.name),
                      onError: (e) => toast.error(toApiError(e).message),
                    })
                  }
                >
                  Rotate Key
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={deactivate.isPending}
                aria-label={`Deactivate ${device.name}`}
                className="text-destructive hover:text-destructive"
              >
                {deactivate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Deactivate device?</AlertDialogTitle>
                <AlertDialogDescription>
                  <strong>{device.name}</strong> will stop being able to push
                  punches and its key is killed. Re-register the device to use
                  it again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() =>
                    deactivate.mutate(device.id, {
                      onSuccess: () => toast.success("Device deactivated"),
                      onError: (e) => toast.error(toApiError(e).message),
                    })
                  }
                >
                  Deactivate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create device dialog
// ---------------------------------------------------------------------------

function CreateDeviceDialog({
  onCreated,
}: {
  readonly onCreated: (rawKey: string, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [vendor, setVendor] = useState<AttDeviceVendor>("generic");
  const [identifier, setIdentifier] = useState("");
  const create = useCreateDevice();

  function submit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give the device a name");
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        vendor: vendor === "generic" ? null : vendor,
        device_identifier: identifier.trim() || null,
      },
      {
        onSuccess: (res) => {
          setOpen(false);
          setName("");
          setVendor("generic");
          setIdentifier("");
          onCreated(res.raw_key, res.device.name);
        },
        onError: (e) => toast.error(toApiError(e).message),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Plus aria-hidden className="h-4 w-4" />
          Register Device
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Register a Push Device</DialogTitle>
            <DialogDescription>
              The device key is shown once after registration.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="dev-name">Device Name</Label>
              <Input
                id="dev-name"
                placeholder="Front-desk ZKTeco"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Vendor</Label>
              <Select
                value={vendor}
                onValueChange={(v) => setVendor(v as AttDeviceVendor)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATT_DEVICE_VENDORS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {ATT_DEVICE_VENDOR_LABELS[v]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dev-id">Device Identifier (Optional)</Label>
              <Input
                id="dev-id"
                placeholder="Serial number / device SN"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                maxLength={120}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || create.isPending}
              className="gap-1.5"
            >
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Register
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// One-time raw-key reveal
// ---------------------------------------------------------------------------

function RevealKeyDialog({
  rawKey,
  deviceName,
  onClose,
}: {
  readonly rawKey: string;
  readonly deviceName: string;
  readonly onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound aria-hidden className="h-4 w-4 text-primary" />
            Device Key: {deviceName}
          </DialogTitle>
          <DialogDescription>
            Copy this key now and configure it on the device. It will not be
            shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 rounded-md border bg-muted px-3 py-2">
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all font-mono text-xs">
              {visible ? rawKey : rawKey.replace(/./g, "•")}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide key" : "Show key"}
            >
              {visible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => copy(rawKey, "Device key")}
              aria-label="Copy key"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Store this key on the device now. Once you close this dialog it cannot
          be recovered — you would need to rotate the key to get a new one.
        </p>
        <DialogFooter>
          <Button onClick={onClose}>Done — I have stored the key</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
